/**
 * The timeline transport (2026-10-16): ONE answer to "where is the running timeline now", read from OPBridge.
 *
 * Operator, 2026-10-16: "when i press launch, the pre start flashing happens, the audio starts and then a bar into
 * the playhead starts" ... "make it gold standard pro grade software". The editor drew its playhead from its own copy
 * of OPBridge's start rule (arm on the next bar line of whatever clock beat it saw first), patched for each clock
 * change. When OPBridge cut the song's pre-roll to 80 ms, a song with no lead-in started its clock just before its
 * bar line, the editor's first look at that clock landed just after it, and it waited a whole bar more (reproduced:
 * smoke timeline_playhead_follows_opbridge, 1.1 bars behind on one run in three). A timeline started anywhere else (a
 * pad, the iPad, a foot pedal) never moved the editor's playhead at all.
 *
 * So nothing here guesses. Per HOME bridge (DEC-224: a timeline plays on one machine) it reads, together:
 *   - `GET /timelines/opbuttons/state`: the engine's own playhead bar, updated every engine tick, PARKED on the start
 *     bar while a start waits for its bar line (opbridge-timelines `current_bar`);
 *   - `GET /clock/status`: the beat on the clock that engine runs on.
 * A Next-bar start, a jump and a section loop anchor on a BAR LINE (`quantized_anchor`), so one moving reading gives the
 * exact anchor: the bar line nearest `beat - (bar - floor(bar)) * 4`, which removes the poll's timing noise entirely.
 * An Instant start anchors where it was pressed, so an estimate that is not near a line is kept as it is. Between
 * readings the playhead runs on a smoothed copy of that clock through the same section rules OPBridge plays
 * (`playheadAt`); a reading that disagrees by more than a tenth of a bar (a jump, a restart) re-anchors at once.
 *
 * EXACT when OPBridge says so (S323, `/health.capabilities.timeline_state_anchor`, asked 2026-10-16): the state then
 * names the run's `songState`, its `anchorBeat` / `anchorBar` and the clock beat of the same engine tick, so nothing
 * above is inferred: no snapping, an Instant start is exact, and a run anchored AHEAD (a song whose intro is a bar or
 * longer starts on its bar 1, a LATER line) starts on its beat and is counted in to it (`transportStartBeat`). The
 * engine and `/clock/status` read one shared clock (a followed song becomes its source), and only `/clock/status` says
 * whether it is moving (`playing && !stale`, the engine's own test), so both are still read. Older bridges get the
 * inference.
 *
 * Cadence: 4 Hz while anything is queued or playing on a followed bridge, 1 Hz otherwise, so a start from anywhere
 * shows within a second (the old shared poll took up to 5 s).
 *
 * One copy per BRIDGE, whatever a caller calls it: '' (the primary) and the primary's id are the same bridge, so they
 * share one reading, one press and one Stop (2026-10-16: the editor keyed it by id and the canvas pads by '', which
 * read it twice and let a Stop in the editor look external to a pad, which then sent a MIDI panic). Read through the
 * PANIC latch: a reading is not a send, and the run's state must stay true while panic holds the sends.
 *
 * Consumers: `followTransport(target)` while shown; `transportRun(target)` (reactive: which timeline, queued /
 * playing / paused) for buttons and states; `transportBar(target, timeline)` per frame for the line itself.
 */
import { createSignal } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import { timelineLoops } from '@opbuttons/schema';
import { getConnection, resolveBridge } from '../opbridge/store.ts';
import type { ClockStatus, TimelineStateSnapshot } from '../opbridge/httpClient.ts';
import { playheadAt } from './playhead.ts';
import { getTimeline } from './store.ts';

/** OPBridge's engine counts four beats to the bar whatever the clock's signature (opbridge-timelines lib.rs). */
const BEATS_PER_BAR = 4;
const FAST_MS = 250;
const IDLE_MS = 1000;
/** A reading this far (bars) from where the playhead would be re-anchors it: a jump, a restart, a missed loop. */
const REANCHOR_BARS = 0.1;
/** How far the bar must have moved past its parked start to count as started (bars). */
const MOVED_BARS = 0.005;
/** A launch pressed here shows as queued at once; if OPBridge never confirms it, it clears after this. */
const PRESS_GRACE_MS = 4000;
/** How long a Stop pressed here outranks readings that still show the run. */
const STOP_GRACE_MS = 2000;

export type RunState = 'queued' | 'playing' | 'paused';

/** What a followed bridge is running (reactive). */
export interface TransportRun {
  timelineId: string;
  state: RunState;
  /** Where it is parked while queued or paused (bars from the timeline's start). */
  parkedBar: number;
  /** A jump waiting for its bar line: the bar it lands on (OPBridge S323 `pendingJumpBar`; null when none, or from a
   *  bridge that does not say). A jump pressed anywhere shows here. */
  pendingJumpBar: number | null;
}

interface ClockModel {
  /** Beat at `at` (performance.now() ms). */
  beat: number;
  at: number;
  /** Beats per second, 0 while the clock is stopped. */
  bps: number;
  /** A small speed correction (1 = none) closing the gap to the last reading in about half a second. */
  rate: number;
  /** Where bar lines fall on this clock, mod 4 (0 for every clock seen so far). */
  lineOffset: number;
}

interface Anchor { bar: number; beat: number }

interface Target {
  refs: number;
  clock: ClockModel | null;
  run: TransportRun | null;
  anchor: Anchor | null;
  /** A press here that OPBridge has not confirmed yet. */
  pressed: { timelineId: string; fromBar: number; until: number } | null;
  /** A Stop pressed here: a reading that still shows this timeline running may have left before OPBridge heard the
   *  stop, so it is not believed until `until`. */
  stopped: { timelineId: string; until: number } | null;
  /** While queued, the exact clock beat it will start on when OPBridge named one (S323 `startLine`). */
  startLine: number | null;
  /** A jump waiting for its bar line: the bar it lands on and the clock beat of that line. The playhead moves there ON
   *  that beat (2026-10-19, operator: "the audio isn't following the playhead properly"): it used to run on from the old
   *  spot until the next reading, up to 250 ms after the song had already jumped. */
  jump: { bar: number; line: number } | null;
}

const targets = new Map<string, Target>();
/** The bridge `target` names ('' = the primary, a row id, a node id), as the one key it is kept under. */
const canon = (target: string): string => resolveBridge(target)?.id ?? target;
const [runs, setRuns] = createSignal<Readonly<Record<string, TransportRun | null>>>({});

/** Which timeline the home bridge `target` is running, and in what state. Reactive. */
export function transportRun(target: string): TransportRun | null {
  return runs()[canon(target)] ?? null;
}

/** Is any timeline queued or playing on any followed bridge? Reactive. (The leave guard asks before closing then.) */
export function anyTimelineRunning(): boolean {
  return Object.values(runs()).some((r) => !!r && r.state !== 'paused');
}

/** Is `timelineId` queued or playing on any followed bridge? (The auto-push sends edits to a running copy live.) */
export function timelineRunning(timelineId: string): boolean {
  for (const tg of targets.values()) if (tg.run && tg.run.timelineId === timelineId && tg.run.state !== 'paused') return true;
  return false;
}

/** The beat on `target`'s clock now (null before its first reading). */
export function transportBeat(target: string, now = performance.now()): number | null {
  const c = targets.get(canon(target))?.clock;
  if (!c) return null;
  return c.beat + c.bps * c.rate * ((now - c.at) / 1000);
}

/** Where `t` is on `target` now, in bars from its start: parked while queued or paused, else the engine's anchor run
 *  forward on the clock through the section rules. Null when `target` is not running `t`. `ended` once a non-looping
 *  run is past its end (OPBridge clears it on its next tick). */
export function transportBar(target: string, t: Timeline, now = performance.now()): { pos: number; ended: boolean; state: RunState } | null {
  const tg = targets.get(canon(target));
  const run = tg?.run;
  if (!tg || !run || run.timelineId !== t.id) return null;
  const beat = transportBeat(target, now);
  // Anchored AHEAD (only OPBridge's own anchor says so): it starts on its beat, with no reading in between.
  const startsNow = run.state === 'queued' && !!tg.anchor && beat !== null && beat >= tg.anchor.beat;
  if ((run.state !== 'playing' && !startsNow) || !tg.anchor) return { pos: run.parkedBar, ended: false, state: run.state };
  if (beat === null) return { pos: tg.anchor.bar, ended: false, state: run.state };
  // A jump whose bar line has come: it is there now, whatever the next reading says a moment later.
  if (tg.jump && run.state === 'playing' && beat >= tg.jump.line) {
    const j = playheadAt(t, tg.jump.bar, (beat - tg.jump.line) / BEATS_PER_BAR, timelineLoops(t));
    return { pos: j.pos, ended: j.ended, state: run.state };
  }
  const at = playheadAt(t, tg.anchor.bar, Math.max(0, beat - tg.anchor.beat) / BEATS_PER_BAR, timelineLoops(t));
  return { pos: at.pos, ended: at.ended, state: startsNow ? 'playing' : run.state };
}

/** A launch pressed here that OPBridge has not answered yet: where it starts is not known (a song brings its own
 *  clock), so nothing should count to it. One reading, at once (`noteTransportLaunch` reads straight away). */
export function transportAwaitingStart(target: string): boolean {
  return !!targets.get(canon(target))?.pressed;
}

/** While `target`'s run is queued: the clock beat it starts on when OPBridge has said (anchored ahead, or a named
 *  start line), else null, and it starts on the next bar line its clock reaches. */
export function transportStartBeat(target: string): number | null {
  const tg = targets.get(canon(target));
  if (!tg?.run || tg.run.state !== 'queued') return null;
  return tg.anchor?.beat ?? tg.startLine;
}

/** What `target`'s run is waiting for and on which clock beat (2026-10-26, the Now playing Display's countdown): a
 *  queued start (on its named beat, else the next bar line of a moving clock) or a jump waiting for its line. `beat` is
 *  null while it cannot be known: a launch pressed here that OPBridge has not answered, or a start waiting on a clock
 *  that is not moving (Live stopped). Compare `beat` with `transportBeat(target)`; a jump past its line has landed. */
export function transportPending(target: string): { kind: 'start' | 'jump'; bar: number; beat: number | null } | null {
  const tg = targets.get(canon(target));
  const run = tg?.run;
  if (!tg || !run) return null;
  if (run.state === 'queued') {
    const now = transportBeat(target);
    const c = tg.clock;
    const beat = tg.pressed ? null : tg.anchor?.beat ?? tg.startLine ?? (c && c.bps > 0 && now !== null ? nextLine(c, now) : null);
    return { kind: 'start', bar: run.parkedBar, beat };
  }
  if (run.state === 'playing' && tg.jump) return { kind: 'jump', bar: tg.jump.bar, beat: tg.jump.line };
  return null;
}

/** What `target` is running, for a status line: the timeline, waiting for its bar line or playing, and the bar now
 *  (1-based, from the engine's anchor, so it is right between readings). Null when nothing runs there or it is paused.
 *  Not reactive to time: a caller showing the bar re-reads it on its own tick. */
export function transportNow(target: string): { timelineId: string; waiting: boolean; bar: number } | null {
  const run = transportRun(target);
  if (!run || run.state === 'paused') return null;
  const t = getTimeline(run.timelineId);
  const at = t ? transportBar(target, t) : null;
  const waiting = (at?.state ?? run.state) === 'queued';
  return { timelineId: run.timelineId, waiting, bar: Math.floor(waiting ? run.parkedBar : (at?.pos ?? run.parkedBar)) + 1 };
}

/** Read `target` (a bridge key; '' = the primary) until the returned function is called. */
export function followTransport(target: string): () => void {
  const key = canon(target);
  const tg = targets.get(key) ?? { refs: 0, clock: null, run: null, anchor: null, pressed: null, stopped: null, startLine: null, jump: null };
  tg.refs++;
  targets.set(key, tg);
  kickTransport();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    tg.refs--;
    if (tg.refs <= 0) {
      targets.delete(key);
      publish();
    }
  };
}

/** OPBridge lands a jump on the first bar line of its clock that it can still reach (`quantized_anchor`, 60 ms ahead of
 *  its 33 ms tick); a request a few hundredths of a second before a line makes that line. */
const JUMP_MARGIN_S = 0.03;
function nextLine(c: ClockModel, beat: number): number {
  const from = beat + c.bps * JUMP_MARGIN_S;
  return Math.ceil((from - c.lineOffset) / BEATS_PER_BAR - 1e-9) * BEATS_PER_BAR + c.lineOffset;
}

/** A jump to `bar` was sent from here while `target` plays: the playhead moves there on the next bar line of its clock,
 *  as OPBridge will, without waiting for a reading to say it did. A reading that disagrees corrects it. */
export function noteTransportJump(target: string, bar: number): void {
  const tg = targets.get(canon(target));
  const beat = transportBeat(target);
  if (!tg?.clock || beat === null || !tg.run || tg.run.state !== 'playing' || tg.clock.bps === 0) return;
  tg.jump = { bar, line: nextLine(tg.clock, beat) };
  kickTransport();
}

/** An INSTANT jump answered (OPBridge S329): the run is at `bar` (fractional) on clock beat `atBeat`, so the playhead
 *  moves there on that beat, a few tens of milliseconds from the press, not on the next bar line. */
export function noteTransportJumpAt(target: string, bar: number, atBeat: number): void {
  const tg = targets.get(canon(target));
  if (!tg?.run || tg.run.state !== 'playing') return;
  tg.jump = { bar, line: atBeat };
  kickTransport();
}

/** A launch was pressed here: show it queued now, and read at once rather than at the next tick. */
export function noteTransportLaunch(target: string, timelineId: string, fromBar = 0): void {
  const key = canon(target);
  const tg = targets.get(key);
  if (!tg) return;
  tg.pressed = { timelineId, fromBar, until: performance.now() + PRESS_GRACE_MS };
  tg.stopped = null;
  lastLocalStop.delete(key);
  if (!tg.run || tg.run.timelineId !== timelineId || tg.run.state === 'paused') {
    tg.run = { timelineId, state: 'queued', parkedBar: fromBar, pendingJumpBar: null };
    tg.anchor = null;
    tg.startLine = null;
  }
  publish();
  kickTransport();
}

/** Did THIS device press Stop on the run of `timelineId` there that has since gone? Every pad and editor asks this
 *  before calling a run that vanished an external stop (2026-10-16: two pads on one timeline; Stop on one made the
 *  other send a MIDI panic, because its own "I stopped it" flag was false). It covers only the run it stopped: any
 *  later run (a launch here, or one first seen from OPBridge) clears it. */
export function stoppedHereRecently(target: string, timelineId: string): boolean {
  const st = lastLocalStop.get(canon(target));
  return !!st && st.timelineId === timelineId && performance.now() - st.at < 30_000;
}
const lastLocalStop = new Map<string, { timelineId: string; at: number }>();

/** One response per run that stops from outside: every pad on that timeline sees it go, and only the first to ask
 *  answers it (2026-10-16: two pads on one timeline each sent a 48-message MIDI panic). */
export function claimExternalStop(target: string, timelineId: string): boolean {
  const key = `${canon(target)}\u0000${timelineId}`;
  const now = performance.now();
  const last = claimedStops.get(key);
  if (last !== undefined && now - last < 10_000) return false;
  claimedStops.set(key, now);
  return true;
}
const claimedStops = new Map<string, number>();

/** A stop was pressed here: show it stopped now (OPBridge confirms on the next reading). */
export function noteTransportStop(target: string): void {
  const key = canon(target);
  const tg = targets.get(key);
  if (!tg) return;
  if (tg.run) lastLocalStop.set(key, { timelineId: tg.run.timelineId, at: performance.now() });
  tg.stopped = tg.run ? { timelineId: tg.run.timelineId, until: performance.now() + STOP_GRACE_MS } : null;
  tg.pressed = null;
  tg.run = null;
  tg.anchor = null;
  tg.startLine = null;
  tg.jump = null;
  publish();
  kickTransport();
}

function publish(): void {
  const next: Record<string, TransportRun | null> = {};
  for (const [k, tg] of targets) next[k] = tg.run ? { ...tg.run } : null;
  const prev = runs();
  const same = Object.keys(next).length === Object.keys(prev).length
    && Object.entries(next).every(([k, r]) => {
      const p = prev[k];
      return (!r && !p) || (!!r && !!p && r.timelineId === p.timelineId && r.state === p.state && r.parkedBar === p.parkedBar && r.pendingJumpBar === p.pendingJumpBar);
    });
  if (!same) setRuns(next);
}

/** How near (beats) an estimated anchor must be to a bar line to be taken as that line. A Next-bar start, a jump and a
 *  section loop anchor ON a line, and the poll's timing noise is a few hundredths of a beat; an Instant start anchors
 *  on the beat it was pressed, which is rarely near a line, and must not be moved there (up to half a bar). */
const SNAP_BEATS = 0.2;

/** The bar line on `c` nearest `beat` when `beat` is that close to it, else `beat` as estimated. */
function snapToLine(c: ClockModel, beat: number): number {
  const line = Math.round((beat - c.lineOffset) / BEATS_PER_BAR) * BEATS_PER_BAR + c.lineOffset;
  return Math.abs(line - beat) <= SNAP_BEATS ? line : beat;
}

function readClock(tg: Target, s: ClockStatus, at: number): void {
  const bps = s.playing && !s.stale ? Math.max(0, s.tempo) / 60 : 0;
  const barStart = s.beat - ((s.beat_in_bar || 1) - 1) - (s.beat - Math.floor(s.beat));
  const lineOffset = ((Math.round(barStart) % BEATS_PER_BAR) + BEATS_PER_BAR) % BEATS_PER_BAR;
  glideClock(tg, s.beat, bps, at, lineOffset);
}

/** Take a clock reading (`beat` at `at`, moving at `bps`) into the smoothed model. */
function glideClock(tg: Target, beat: number, bps: number, at: number, lineOffset: number): void {
  const c = tg.clock;
  if (!c || bps === 0 || c.bps === 0 || Math.abs(bps - c.bps) > c.bps * 0.01) {
    tg.clock = { beat, at, bps, rate: 1, lineOffset };
    return;
  }
  const predicted = c.beat + c.bps * c.rate * ((at - c.at) / 1000);
  const err = beat - predicted;
  // A clock that changed source (a song taking it, its count starting from 0) or jumped: take it as read.
  if (Math.abs(err) > 0.5) { tg.clock = { beat, at, bps, rate: 1, lineOffset }; return; }
  // Glide: carry on from where the line is, a little faster or slower, never backwards (the SongMarker rule).
  const rate = Math.max(0.9, Math.min(1.1, 1 + err / (bps * 0.5)));
  tg.clock = { beat: predicted, at, bps, rate, lineOffset };
}

/** A reading from a bridge that names its run's anchor (S323): taken as said, nothing inferred. The clock is the beat
 *  of the same engine tick (so it is on the anchor's own clock), moving when `/clock/status` says the clock is. False
 *  when there is no run with those fields, and the inference reads it instead. */
function readExact(tg: Target, snap: TimelineStateSnapshot, clock: ClockStatus | null, at: number): boolean {
  const id = snap.activeSongId;
  const bar = snap.songPlayheadBar;
  const state = snap.songState;
  if (!id || typeof bar !== 'number' || state === undefined) return false;
  if (typeof snap.clockBeat === 'number' && typeof snap.bpm === 'number') {
    // A Play-now run (OPBridge S340, `freeBpm`) moves on its OWN clock while `/clock/status` says Live is stopped: its
    // `clockBeat` and `bpm` are that clock's, so it moves whatever the shared clock says. Read as stopped, the playhead
    // stood still between readings and jumped four times a second.
    const moving = typeof snap.freeBpm === 'number' || (clock ? clock.playing && !clock.stale : true);
    glideClock(tg, snap.clockBeat, moving ? Math.max(0, snap.bpm) / 60 : 0, at, tg.clock?.lineOffset ?? 0);
  } else if (clock) readClock(tg, clock, at);
  const now = performance.now();
  if (tg.stopped && (now >= tg.stopped.until || id !== tg.stopped.timelineId)) tg.stopped = null;
  if (tg.stopped) return true;
  if (tg.pressed && tg.pressed.timelineId === id) tg.pressed = null;
  const prev = tg.run && tg.run.timelineId === id ? tg.run : null;
  // First sight of this run (or a paused one going again): a stop pressed here was about an earlier run.
  if (state !== 'paused' && (!prev || prev.state === 'paused')) lastLocalStop.delete([...targets.entries()].find(([, v]) => v === tg)?.[0] ?? '');
  const anchored = state !== 'paused' && typeof snap.anchorBeat === 'number' && typeof snap.anchorBar === 'number';
  const newAnchor = anchored ? { bar: snap.anchorBar!, beat: snap.anchorBeat! } : null;
  // A pending jump OPBridge names: the line it lands on, worked from this reading's clock unless one is noted already
  // for that bar. None pending: it has landed (the anchor now says so) or was replaced.
  if (state === 'playing' && typeof snap.pendingJumpBar === 'number' && tg.clock) {
    // An instant jump names its own beat (S329 `pendingJumpBeat`); a bar-line jump lands on the clock's next line.
    const line = typeof snap.pendingJumpBeat === 'number' ? snap.pendingJumpBeat : null;
    if (!tg.jump || tg.jump.bar !== snap.pendingJumpBar || (line !== null && tg.jump.line !== line)) tg.jump = { bar: snap.pendingJumpBar, line: line ?? nextLine(tg.clock, snap.clockBeat ?? tg.clock.beat) };
  } else if (!newAnchor || !tg.anchor || newAnchor.beat !== tg.anchor.beat || newAnchor.bar !== tg.anchor.bar || state !== 'playing'
    || (tg.jump && typeof snap.clockBeat === 'number' && snap.clockBeat > tg.jump.line + BEATS_PER_BAR)) {
    tg.jump = null;
  }
  tg.anchor = newAnchor;
  tg.startLine = state === 'queued' && typeof snap.startLine === 'number' ? snap.startLine : null;
  // Where it started stays put while it plays (the bar moves every reading, and `runs` would change every tick).
  tg.run = {
    timelineId: id, state, parkedBar: state === 'playing' && prev && prev.state !== 'paused' ? prev.parkedBar : bar,
    pendingJumpBar: typeof snap.pendingJumpBar === 'number' ? snap.pendingJumpBar : null,
  };
  return true;
}

function readState(tg: Target, snap: TimelineStateSnapshot, at: number): void {
  const now = performance.now();
  if (tg.stopped && (now >= tg.stopped.until || snap.activeSongId !== tg.stopped.timelineId)) tg.stopped = null;
  if (tg.stopped) return;
  const id = snap.activeSongId;
  const bar = typeof snap.songPlayheadBar === 'number' ? snap.songPlayheadBar : null;
  if (!id || bar === null) {
    // Nothing runs there. A press here not yet seen by OPBridge stays queued until its grace ends.
    if (tg.pressed && now < tg.pressed.until) return;
    tg.pressed = null; tg.run = null; tg.anchor = null;
    return;
  }
  if (tg.pressed && tg.pressed.timelineId === id) tg.pressed = null;
  if (!snap.songIsPlaying) {
    tg.run = { timelineId: id, state: 'paused', parkedBar: bar, pendingJumpBar: null };
    tg.anchor = null;
    return;
  }
  if (!tg.run || tg.run.timelineId !== id || tg.run.state === 'paused') {
    // First sight of this run: queued at `bar` until the bar moves. A stop pressed here was about an earlier run.
    lastLocalStop.delete([...targets.entries()].find(([, v]) => v === tg)?.[0] ?? '');
    tg.run = { timelineId: id, state: 'queued', parkedBar: bar, pendingJumpBar: null };
    tg.anchor = null;
    return;
  }
  const c = tg.clock;
  const beatThen = c ? c.beat + c.bps * c.rate * ((at - c.at) / 1000) : null;
  if (tg.run.state === 'queued') {
    if (bar <= tg.run.parkedBar + MOVED_BARS) { tg.run = { ...tg.run, parkedBar: bar }; return; }
    tg.run = { timelineId: id, state: 'playing', parkedBar: tg.run.parkedBar, pendingJumpBar: null };
  }
  if (!c || beatThen === null) return;
  const anchorFrom = (): Anchor => {
    const whole = Math.floor(bar + 1e-6);
    return { bar: whole, beat: snapToLine(c, beatThen - (bar - whole) * BEATS_PER_BAR) };
  };
  if (!tg.anchor) { tg.anchor = anchorFrom(); return; }
  // Where the anchor says the run is at the reading's instant; a real disagreement (a jump, a restart) re-anchors.
  if (Math.abs(predictedBar(tg, beatThen) - bar) > REANCHOR_BARS) { tg.anchor = anchorFrom(); tg.jump = null; }
  // A jump noted here whose line passed a bar ago without a reading confirming it: the reading is the truth.
  else if (tg.jump && beatThen > tg.jump.line + BEATS_PER_BAR) tg.jump = null;
}

/** The reading-time position the current anchor implies, through the section rules of the run's timeline when this
 *  device has it, else straight on. */
function predictedBar(tg: Target, beat: number): number {
  if (!tg.anchor || !tg.run) return Number.NaN;
  const t = getTimeline(tg.run.timelineId);
  const elapsed = Math.max(0, beat - tg.anchor.beat) / BEATS_PER_BAR;
  return t ? playheadAt(t, tg.anchor.bar, elapsed, timelineLoops(t)).pos : tg.anchor.bar + elapsed;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight = false;
let kickAgain = false;

/** Read every followed bridge now. */
export function kickTransport(): void {
  if (inFlight) { kickAgain = true; return; }
  if (timer !== null) { clearTimeout(timer); timer = null; }
  void tick();
}

async function tick(): Promise<void> {
  if (targets.size === 0) return;
  inFlight = true;
  await Promise.all([...targets.entries()].map(async ([key, tg]) => {
    const cfg = resolveBridge(key);
    const conn = cfg ? getConnection(cfg.id) : null;
    if (!conn || conn.status() !== 'connected') return;
    const http = conn.httpClient();
    const sent = performance.now();
    // The clock only matters while something runs there (idle: one small state read a second per bridge).
    const needClock = !!tg.run || !!tg.pressed;
    const exact = conn.health()?.capabilities?.['timeline_state_anchor'] === true;
    const [clock, state] = await Promise.all([
      needClock ? http.clockStatus().catch(() => null) : Promise.resolve(null),
      http.getTimelineState('opbuttons').catch(() => null),
    ]);
    const at = (sent + performance.now()) / 2;
    if (!targets.has(key)) return;
    const wasIdle = !tg.run;
    if (!(exact && state && readExact(tg, state, clock, at))) {
      if (clock) readClock(tg, clock, at);
      if (state) readState(tg, state, at);
    }
    // A run just appeared while idle: read the clock now rather than a second later.
    if (wasIdle && tg.run) kickAgain = true;
  }));
  publish();
  inFlight = false;
  if (kickAgain) { kickAgain = false; void tick(); return; }
  const busy = [...targets.values()].some((tg) => !!tg.run);
  if (targets.size > 0) timer = setTimeout(() => { timer = null; void tick(); }, busy ? FAST_MS : IDLE_MS);
}
